import { CloseOutlined } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { DynamicLayout, PimoDynamicReactLayout } from "@pimo/pimo-app-builder";
import React from "react";
import { useLocation, useParams } from "react-router-dom";

import { DialogLayoutSideBar } from "./layout-sidebar";
import { DialogLayoutType } from "./types";

export type DialogLayoutProps = {
  viewname?: string;
};

const Stage = styled(Box, {
  shouldForwardProp: (prop) => prop !== "enablePageBreak",
})<{ enablePageBreak: boolean }>(({ theme, enablePageBreak }) => ({
  flexGrow: 1,
  ...theme.mixins.toolbar,
  backgroundColor: theme.palette.common.white,

  "@media print": {
    backgroundColor: "unset",
    pageBreakBefore: enablePageBreak ? "always" : "avoid",
  },
}));

const DialogLayoutReactComponent: PimoDynamicReactLayout<
  DialogLayoutType,
  "dialog:close" | "dialog:navigate",
  { currentPath: string },
  DialogLayoutProps
> = ({ entries, title, icon, components, nodes, fireEvent }) => {
  const theme = useTheme();
  const params = useParams<{ viewname?: string }>();
  const location = useLocation();

  const routeViewname = params.viewname;
  const currentPath = routeViewname
    ? location.pathname // Use actual current path instead of hardcoding
    : entries?.[0]?.items?.[0]?.link || entries?.[0]?.link || undefined;

  const [viewName, setViewName] = React.useState<string | undefined>(
    currentPath
  );
  const [activeTab, setActiveTab] = React.useState<string | null>(
    currentPath || null
  );

  React.useEffect(() => {
    if (currentPath) {
      setViewName(currentPath);
      setActiveTab(currentPath);
    }
  }, [currentPath]);

  const viewComponents = viewName
    ? components.filter((component) => {
        const componentViewname = component.layoutProps?.viewname as string;
        if (!componentViewname) return false;
        return viewName.includes(componentViewname);
      })
    : components;

  return (
    <Dialog open={true} fullWidth fullScreen>
      <DialogTitle
        sx={{
          width: "100%",
          display: "flex",
          height: "83px",
          p: 0,
          justifyContent: "space-between",
          backgroundColor: "white",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            padding: 2,
            width: "100%",
            display: "flex",
            height: "100%",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              component="img"
              sx={{
                content: {
                  xs: icon?.small ? `url(${icon.small})` : "",
                  sm: icon?.big ? `url(${icon.big})` : "",
                },
              }}
              alt={icon?.alt ?? ""}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              {title}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
            }}
          >
            {nodes?.map((node, index) => (
              <Box key={index} sx={{ marginLeft: theme.spacing(1) }}>
                {node}
              </Box>
            ))}
            <IconButton
              onClick={() => {
                fireEvent?.("dialog:close");
              }}
              sx={{
                color: "#000000",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseOutlined></CloseOutlined>
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 0, width: "100%" }}>
        <Box
          sx={{
            height: "calc(100vh - 83px)",
            display: "flex",
            flexDirection: "row",
          }}
        >
          {entries && (
            <Box
              sx={{
                backgroundColor: theme.palette.secondary.main,
                p: 2,
                flexGrow: 1,
                height: "auto",
                minWidth: "300px",
                maxWidth: "300px",
                justifyContent: "flex-start",
                minHeight: "calc(100vh - 83px)",
                msOverflowStyle: "none",
                width: "300px",
                overflowY: "scroll",
                "&::-webkit-scrollbar": {
                  width: "0px",
                  background: "transparent",
                },
                scrollbarWidth: "none",
              }}
            >
              <DialogLayoutSideBar
                entries={entries}
                onNavigate={(link) => {
                  setViewName(link);
                  setActiveTab(link);
                }}
                activeTab={activeTab}
              />
            </Box>
          )}
          <Box
            sx={{
              backgroundColor: theme.palette.secondary.main,
              minHeight: "calc(100vh - 83px)",
              minWidth: "calc(100vw - 300px)",

              overflow: "hidden",
            }}
          >
            <Stage
              enablePageBreak={false}
              sx={{
                height: "auto",
                width: "100%",
                overflow: "clip",
              }}
            >
              <Grid2 container spacing={2}>
                {viewComponents.map((component, index) => {
                  const Component = component?.render?.();
                  return (
                    <Grid2
                      key={`grid_item_${index}`}
                      sx={{ width: "100%" }}
                      {...component.layoutProps}
                    >
                      <Component />
                    </Grid2>
                  );
                })}
              </Grid2>
            </Stage>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export class DialogLayout extends DynamicLayout<
  DialogLayoutType,
  "dialog:close" | "dialog:navigate",
  { currentPath: string },
  DialogLayoutProps
> {
  getLayoutComponent(): PimoDynamicReactLayout<
    DialogLayoutType,
    "dialog:close" | "dialog:navigate",
    { currentPath: string },
    DialogLayoutProps
  > {
    return DialogLayoutReactComponent;
  }
}

export default DialogLayout;
